import { Loader2 } from 'lucide-react'
import { Button } from '@klear/ui/components/button'
import type { NextStep } from '@klear/schema/document-review-step'

export const APPROVE_KEY = 'approve'

const APPROVABLE_STEPS: readonly NextStep[] = ['readyToApprove', 'waitingOnYou']

export function canDecideStep(step: NextStep): boolean {
  return APPROVABLE_STEPS.includes(step) || step === 'waitingOnParent'
}

export type CommitHold = {
  step: NextStep
  pendingParent: boolean
  canEdit: boolean
  editing: boolean
}

const HOLD_BY_STEP: Record<NextStep, string | null> = {
  readyToApprove: null,
  waitingOnYou: null,
  waitingOnParent: 'Create records stays closed until the parent this document names is decided.',
  superseded: 'Another run replaced this one, so there is nothing left to decide here.',
  filedByLaterRun: 'A later run filed this document, so there is nothing left to decide here.',
  creatingRecords: 'Klear is creating the records, so there is nothing to decide.',
  processing: 'The document is still processing.',
  blocked: 'This run needs processing again, keying in, or dismissing.',
  dismissed: 'This document was dismissed, so there is nothing to decide.',
  filed: 'The records exist, so there is nothing left to decide.',
  filedAutomatically: 'Klear filed this document on its own, so there is nothing left to decide.',
  notProcessed: 'This document has not been processed yet.'
}

export function holdReason(hold: CommitHold): string | null {
  if (!hold.canEdit) return 'Deciding a review needs source-document edit. Ask a super admin or a CSM to take this one.'
  const byStep = HOLD_BY_STEP[hold.step]
  if (byStep !== null) return byStep
  if (hold.pendingParent) return HOLD_BY_STEP.waitingOnParent
  if (hold.editing) return 'Finish the correction you are typing first.'
  return null
}

export type ApproveRowProps = {
  holdReason: string | null
  outstanding: number
  approvableCount: number
  canEdit: boolean
  step: NextStep
  canCreate: boolean
  isApproving: boolean
  isRejecting: boolean
  onApprove: () => void
  onReject: () => void
  onNextOpen: () => void
}

function stateText(holdReason: string | null, outstanding: number, approvableCount: number): string {
  if (holdReason !== null) return holdReason
  if (outstanding === 1) return 'Confirm the last value to create the records'
  if (outstanding > 1) return `Confirm ${outstanding} values to create the records`
  if (approvableCount === 0) return 'Nothing to confirm'
  return approvableCount === 1 ? 'The value is confirmed' : `All ${approvableCount} values confirmed`
}

export function ApproveRow({
  holdReason,
  outstanding,
  approvableCount,
  canEdit,
  step,
  canCreate,
  isApproving,
  isRejecting,
  onApprove,
  onReject,
  onNextOpen
}: ApproveRowProps) {
  const busy = isApproving || isRejecting
  const allConfirmed = holdReason === null && outstanding === 0

  return (
    <div className="bg-card flex shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-lg border px-4 py-3">
      <p className="text-muted-foreground flex min-w-0 items-center gap-2 text-sm" aria-live="polite">
        {isApproving ? (
          <>
            <Loader2 className="h-4 w-4 shrink-0 animate-spin motion-reduce:animate-none" aria-hidden />
            <span>Creating records…</span>
          </>
        ) : (
          <span>{stateText(holdReason, outstanding, approvableCount)}</span>
        )}
        {outstanding > 0 && holdReason === null && (
          <Button variant="ghost" size="xs" onClick={onNextOpen}>
            Next to confirm
          </Button>
        )}
      </p>
      <div className="flex shrink-0 flex-row-reverse items-center gap-2">
        {allConfirmed && (
          <Button size="sm" data-approval-key={APPROVE_KEY} disabled={!canCreate} onClick={onApprove}>
            Create records
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={onReject} disabled={!canEdit || !canDecideStep(step) || busy}>
          Reject…
        </Button>
      </div>
    </div>
  )
}
